import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { ValidationError } from '~/errors';
import { validateId } from '~/repositories/utils';
import { maintenanceServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { VendorDto, VendorOverviewDto, VendorPortalInviteResponseDto, VendorsDto } from '~/swagger/maintenance/Api';
import type { CreateVendorPaymentAccountDto, HydratedVendorDto, PaymentAccountDto, PaymentAccountsDto } from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';
import type { CreateOrUpdateVendor, ValidatedVendor } from '~/types/vendorModels';

const VENDOR_URL = `${maintenanceServiceUrl()}/company-portal/vendors`;
// Payment accounts are owned by the Payments service (exposed via Portal), not Maintenance.
const PORTAL_VENDOR_URL = '/api/vendors';

export function validateVendor(vendor: ValidatedVendor): void {
  if (!vendor) throw new ValidationError('Vendor object must be provided');
  if (!vendor.name) throw new ValidationError('Vendor name must be provided');

  // You can add more validations based on your business rules.
}

export type VendorFilter = ApiFilter<{
  categoryId?: string;
  phoneNumber?: string;
}>;

// The Maintenance service now paginates with the generic PaginationDto (`page`);
// map it back to the UI's `currentPage`-based shape so consumers stay unchanged.
type VendorsPage = { items: VendorDto[]; totalCount: number; totalPages: number; page: number; pageSize: number };

const toVendorsDto = (page: VendorsPage): VendorsDto => ({
  currentPage: page.page,
  totalPages: page.totalPages,
  pageSize: page.pageSize,
  totalCount: page.totalCount,
  items: page.items,
});

export class VendorRepository extends BaseRestRepository {
  public async getVendors(filter?: VendorFilter): Promise<VendorsDto> {
    const url = urlWithQuery(VENDOR_URL, filter);
    const response = await this.fetchWithAuth(url);
    return toVendorsDto((await this.getJsonResponse(response)) as VendorsPage);
  }

  public async getAllVendors(filter?: VendorFilter): Promise<VendorsDto> {
    const url = urlWithQuery(`${VENDOR_URL}/all`, filter);
    const response = await this.fetchWithAuth(url);
    return toVendorsDto((await this.getJsonResponse(response)) as VendorsPage);
  }

  public async getVendor(vendorId: string): Promise<HydratedVendorDto> {
    validateId(vendorId);
    const url = `${VENDOR_URL}/${vendorId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createVendor(vendor: CreateOrUpdateVendor): Promise<HydratedVendorDto> {
    validateVendor(vendor);
    const url = VENDOR_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(vendor),
    });
    return this.getJsonResponse(response);
  }

  public async updateVendor(vendorId: string, vendor: CreateOrUpdateVendor): Promise<HydratedVendorDto> {
    validateId(vendorId);
    validateVendor(vendor);
    const url = `${VENDOR_URL}/${vendorId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(vendor),
    });
    return this.getJsonResponse(response);
  }

  public async deleteVendor(vendorId: string): Promise<void> {
    validateId(vendorId);
    if (!vendorId) throw new ValidationError('HydratedVendorDto ID must be provided');
    const url = `${VENDOR_URL}/${vendorId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      console.error(await response.json());
    }
  }

  public async getVendorOverview(vendorId: string, useUpdatedAt?: boolean): Promise<VendorOverviewDto> {
    let url = `${VENDOR_URL}/${vendorId}/overview`;
    if (useUpdatedAt) {
      url += `?updatedAt=` + todayWithSystemTimeZone().valueOf();
    }
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async batchGetVendorsByIds(vendorIds: string[]): Promise<VendorDto[]> {
    const url = urlWithQuery(`${VENDOR_URL}/batch`, { vendorIds });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getVendorPaymentAccounts(vendorId: string): Promise<PaymentAccountsDto> {
    const url = `${PORTAL_VENDOR_URL}/${vendorId}/payment-accounts`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addVendorPaymentAccount(vendorId: string, payload: CreateVendorPaymentAccountDto): Promise<PaymentAccountDto> {
    const url = `${PORTAL_VENDOR_URL}/${vendorId}/payment-accounts`;
    const response = await this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(payload) });
    return this.getJsonResponse(response);
  }

  public async deleteVendorPaymentAccount(vendorId: string, paymentAccountId: string): Promise<void> {
    const url = `${PORTAL_VENDOR_URL}/${vendorId}/payment-accounts/${paymentAccountId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async portalInvite(vendorId: string): Promise<VendorPortalInviteResponseDto> {
    const url = `${VENDOR_URL}/${vendorId}/portal-invite`;
    const response = await this.fetchWithAuth(url, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async changeVendorPassword(vendorId: string, password: string): Promise<void> {
    const url = `${VENDOR_URL}/${vendorId}/password`;
    await this.fetchWithAuth(url, { method: 'PUT', body: JSON.stringify({ password }) });
  }

  public async deactivateVendor(vendorId: string): Promise<HydratedVendorDto> {
    validateId(vendorId);
    const url = `${VENDOR_URL}/${vendorId}/deactivate`;
    const response = await this.fetchWithAuth(url, { method: 'DELETE' });
    return this.getJsonResponse(response);
  }

  public async reactivateVendor(vendorId: string): Promise<HydratedVendorDto> {
    validateId(vendorId);
    const url = `${VENDOR_URL}/${vendorId}/reactivate`;
    const response = await this.fetchWithAuth(url, { method: 'PUT' });
    return this.getJsonResponse(response);
  }
}

export const vendorRepo = new VendorRepository();

// Shared, session-cached vendors. The "all vendors" list and any by-id fetch both
// populate a per-id cache, so a given vendor is fetched at most once per session
// (active vendors arrive via the all-vendors list; inactive ones via a batch fetch).
let allVendorsCache: Promise<VendorDto[]> | undefined;
const vendorByIdCache = new Map<string, VendorDto>();

export const getCachedAllVendors = (): Promise<VendorDto[]> => {
  if (!allVendorsCache) {
    allVendorsCache = vendorRepo.getAllVendors().then((page) => {
      for (const vendor of page.items) vendorByIdCache.set(vendor.id, vendor);
      return page.items;
    });
  }
  return allVendorsCache;
};

// Resolve vendors by id from the per-id cache, batch-fetching only the ones not
// already cached. When the all-vendors list has been loaded (board/lists), its
// vendors are already cached, so those resolve with no network call.
export const resolveVendorsByIds = async (ids: string[]): Promise<VendorDto[]> => {
  if (ids.length === 0) return [];
  const missing = ids.filter((id) => !vendorByIdCache.has(id));
  if (missing.length > 0) {
    for (const vendor of await vendorRepo.batchGetVendorsByIds(missing)) {
      vendorByIdCache.set(vendor.id, vendor);
    }
  }
  return ids.map((id) => vendorByIdCache.get(id)).filter((vendor): vendor is VendorDto => !!vendor);
};

export const invalidateVendorsCache = (): void => {
  allVendorsCache = undefined;
  vendorByIdCache.clear();
};
